import { useContext, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    ClickAwayListener,
    Dialog,
    FormControl,
    FormLabel,
    Grid,
    InputAdornment,
    MenuItem,
    Typography
} from '@mui/material';
import { ArrowBackIos, Circle, RadioButtonUnchecked, RadioButtonChecked } from '@mui/icons-material';
import { DatePicker } from 'formik-mui-lab';
import { Formik, Form, Field } from 'formik';
import { LocalizationProvider } from '@mui/lab';
import { Select, TextField } from 'formik-mui';
import * as Yup from 'yup';
import AdapterDayjs from '@mui/lab/AdapterDayjs';
import cloneDeep from 'lodash/cloneDeep';
// Our Components
import { CardContext } from 'context/cards/cardContext';
import { SHOW_CARD_FORM_MODAL, CLOSE_USER_FORM_MODAL } from 'context/cards/types';
// Services
import { Users } from 'lib/api';
// Utils and Helpers
import { USER_ROLES, US_PHONE_NUMBER_REGEX, FIRST_LAST_NAME_REGEX, EMAIL_REGEX, US_SSN_REGEX } from 'helpers/constants';
import { isAdult } from 'helpers';
// Assets
import { ReactComponent as GroupIcon } from 'assets/userForm/Group.svg';
import { ReactComponent as SettingsIcon } from 'assets/userForm/Settings.svg';
import './CreateUserForm.css';

const INITIAL_VALUES = {
    firstName: '',
    lastName: '',
    jobTitle: '',
    department: '',
    dateOfBirth: '',
    idType: '',
    idNumber: '',
    email: '',
    phoneNumber: '',
    id_role: USER_ROLES.ADMIN
};

const userSchema = Yup.object().shape({
    firstName: Yup.string()
        .matches(FIRST_LAST_NAME_REGEX, { message: 'No special characters allowed' })
        .max(255, 'Max. 255 characters')
        .required('Required'),
    lastName: Yup.string()
        .matches(FIRST_LAST_NAME_REGEX, { message: 'No special characters allowed' })
        .max(255, 'Max. 255 characters')
        .required('Required'),
    jobTitle: Yup.string().max(255, 'Max. 255 characters').optional(),
    department: Yup.string().max(255, 'Max. 255 characters').optional(),
    email: Yup.string().matches(EMAIL_REGEX, { message: 'Please add a valid business email' }).required('Required'),
    phoneNumber: Yup.string().matches(US_PHONE_NUMBER_REGEX, { message: 'Invalid phone number' }).required('Required'),
    idType: Yup.string().required('Required'),
    idNumber: Yup.string()
        .required('Required')
        .when('idType', {
            is: 'ssn',
            then: Yup.string().matches(US_SSN_REGEX, { message: 'Invalid SSN number.' }),
            otherwise: Yup.string().trim().length(9, 'ID number must have 9 characters')
        }),
    dateOfBirth: Yup.date()
        .typeError('Invalid date')
        .test('dateOfBirth', 'Invalid date. The user must be at least 18 years old', dateOfBirth => {
            return isAdult(dateOfBirth);
        })
        .required()
});
const CreateUserForm = () => {
    const { dispatch, isUserFormModalOpen } = useContext(CardContext);
    const [errorMessage, setErrorMessage] = useState('');
    const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);

    const handleCreateUserSubmit = async (values, actions) => {
        const formValues = cloneDeep(values);
        try {
            formValues.phoneNumber = `+1${formValues.phoneNumber}`;
            await Users.createUser(formValues);

            dispatch({ type: CLOSE_USER_FORM_MODAL });
            dispatch({ type: SHOW_CARD_FORM_MODAL });
        } catch (error) {
            if (error.response) {
                const response = error.response.data;
                if (response.type === 'Users/PhoneNumberExists') {
                    return actions.setFieldError('phoneNumber', response.message);
                }
                if (response.type === 'Users/EmailExists') {
                    return actions.setFieldError('email', response.message);
                }
                if (response.type === 'Users/ProviderError') {
                    return setErrorMessage(response.message);
                }
            } else {
                setErrorMessage('An unexpected error has occurred. Please try again later.');
            }
        }
    };

    const handleUserRoleClick = (userRole, setFieldValue) => {
        setFieldValue('id_role', userRole);
    };

    const handleBackBtnClick = () => {
        dispatch({ type: CLOSE_USER_FORM_MODAL });
        dispatch({ type: SHOW_CARD_FORM_MODAL });
    };

    return (
        <Dialog open={isUserFormModalOpen} onClose={() => {}} fullWidth maxWidth="md">
            <Box className="create-user-form">
                <Box className="create-card__modal-title">Select the cardholder</Box>
                <Box className="create-card__modal-paginator">
                    <span className="create-card__modal-page-indicator">2/4 Steps</span>
                    <Circle className="create-card__indicator-active" />
                    <Circle className="create-card__indicator-active" />
                    <Circle className="create-card__indicator-inactive" />
                    <Circle className="create-card__indicator-inactive" />
                </Box>
                <Typography variant="div" className="create-user-form__title">
                    Create a new cardholder
                </Typography>
                <Formik
                    initialValues={INITIAL_VALUES}
                    enableReinitialize={true}
                    validationSchema={userSchema}
                    onSubmit={handleCreateUserSubmit}>
                    {({ setFieldValue, isSubmitting, values }) => (
                        <Form id="customize-card-form">
                            <Grid container rowSpacing={1} columnSpacing={2} sx={{ marginTop: '8px' }}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormLabel required className="create-user-form__form-label">
                                            First Name
                                        </FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="firstName"
                                            type="text"
                                            size="small"
                                            placeholder="Enter name"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormLabel required className="create-user-form__form-label">
                                            Last Name
                                        </FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="lastName"
                                            type="text"
                                            size="small"
                                            placeholder="Enter last name"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormLabel className="create-user-form__form-label">Job Title</FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="jobTitle"
                                            type="text"
                                            size="small"
                                            placeholder="Enter job title"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormLabel className="create-user-form__form-label">Department</FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="department"
                                            type="text"
                                            size="small"
                                            placeholder="Enter department"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ClickAwayListener onClickAway={() => setIsDatepickerOpen(false)}>
                                        <FormControl fullWidth required>
                                            <FormLabel className="create-user-form__form-label">Date of Birth</FormLabel>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <Field
                                                    component={DatePicker}
                                                    variant="outlined"
                                                    name="dateOfBirth"
                                                    mask="__/__/____"
                                                    open={isDatepickerOpen}
                                                    onAccept={() => setIsDatepickerOpen(false)}
                                                    textField={{
                                                        size: 'small',
                                                        onClick: () => setIsDatepickerOpen(isDatepickerOpen => !isDatepickerOpen)
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </ClickAwayListener>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormLabel className="create-user-form__form-label">ID Number*</FormLabel>
                                    <Grid container columnSpacing={1}>
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth required>
                                                <Field component={Select} name="idType" size="small">
                                                    <MenuItem value="passport">Passport</MenuItem>
                                                    <MenuItem value="ssn">SSN</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={8}>
                                            <FormControl fullWidth>
                                                <Field
                                                    component={TextField}
                                                    variant="outlined"
                                                    name="idNumber"
                                                    type="text"
                                                    size="small"
                                                    placeholder="Enter number"
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth required>
                                        <FormLabel className="create-user-form__form-label">Email Address</FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="email"
                                            type="text"
                                            size="small"
                                            placeholder="Enter email address"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth required>
                                        <FormLabel className="create-user-form__form-label">Phone Number</FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="phoneNumber"
                                            type="text"
                                            size="small"
                                            placeholder="Enter phone number"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Typography>+1</Typography>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Box marginTop="16px">
                                <FormLabel required className="card-input-label">
                                    Select Role
                                </FormLabel>
                                <Box
                                    className={`create-user-form__role ${
                                        values.id_role === USER_ROLES.ADMIN && 'create-user-form__role-checked'
                                    }`}
                                    onClick={() => handleUserRoleClick(USER_ROLES.ADMIN, setFieldValue)}>
                                    <Box display="flex">
                                        {values.id_role === USER_ROLES.ADMIN ? (
                                            <RadioButtonChecked sx={{ color: '#1BABE2', marginRight: '12px' }} />
                                        ) : (
                                            <RadioButtonUnchecked sx={{ color: '#909DA6', marginRight: '12px' }} />
                                        )}
                                        <Box>
                                            <Typography component="div" className="create-user-form__role-name">
                                                Administrator
                                            </Typography>
                                            <Typography component="div" className="create-user-form__role-description">
                                                Can create and edit cards
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box alignSelf="center">
                                        <SettingsIcon />
                                    </Box>
                                </Box>
                                <Box
                                    className={`
                                                create-user-form__role${' '}
                                                ${values.id_role === USER_ROLES.CARD_HOLDER && 'create-user-form__role-checked'}`}
                                    onClick={() => handleUserRoleClick(USER_ROLES.CARD_HOLDER, setFieldValue)}>
                                    <Box display="flex">
                                        {values.id_role === USER_ROLES.CARD_HOLDER ? (
                                            <RadioButtonChecked sx={{ color: '#1BABE2', marginRight: '12px' }} />
                                        ) : (
                                            <RadioButtonUnchecked sx={{ color: '#909DA6', marginRight: '12px' }} />
                                        )}
                                        <Box>
                                            <Typography component="div" className="create-user-form__role-name">
                                                Cardholder
                                            </Typography>
                                            <Typography component="div" className="create-user-form__role-description">
                                                Can see and use cards in their name
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box alignSelf="center">
                                        <GroupIcon />
                                    </Box>
                                </Box>
                            </Box>
                            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                            <Box display="flex" justifyContent="space-between" sx={{ marginTop: '20px' }}>
                                <Button variant="outlined" onClick={handleBackBtnClick} disabled={isSubmitting}>
                                    <ArrowBackIos fontSize="small" />
                                </Button>
                                <Button variant="contained" type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? <CircularProgress size="20px" /> : 'Create'}
                                </Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Box>
        </Dialog>
    );
};

export default CreateUserForm;
